import { Alert, Box, Paper } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import React from 'react';
import { widgetRegistry } from '../canvas/utils/widgetRegistry.js';
import {
  CanvasMode,
  type Widget,
  type WidgetConfig,
  type WidgetProps,
  type WidgetState,
} from '../canvas/types/index.js';

interface WidgetHostProps {
  type: string;
  config?: Partial<WidgetConfig>;
  inputs?: Record<string, any>;
  onOutputsChange?: (outputs: Record<string, any>) => void;
  onStateChange?: (state: WidgetState) => void;
  disableCard?: boolean;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
}

const createWidgetInstance = (
  definition: ReturnType<typeof widgetRegistry.get>,
  hostId: string,
  config: Partial<WidgetConfig> | undefined,
  inputs: Record<string, any> | undefined,
  previous?: Widget
): Widget => {
  if (!definition) {
    throw new Error('Widget definition missing');
  }

  const baseInputs = definition.inputDefinitions.map((inputDef) => {
    const prev = previous?.inputs.find((input) => input.id === inputDef.id);
    const value = inputs?.[inputDef.id] ?? prev?.value ?? inputDef.defaultValue;
    return {
      id: inputDef.id,
      name: inputDef.name,
      type: inputDef.type,
      schema: inputDef.schema,
      acceptsMultiple: inputDef.acceptsMultiple,
      connected: undefined,
      value,
    };
  });

  const baseOutputs = definition.outputDefinitions.map((outputDef) => {
    const prev = previous?.outputs.find((output) => output.id === outputDef.id);
    return {
      id: outputDef.id,
      name: outputDef.name,
      type: outputDef.type,
      schema: outputDef.schema,
      value: prev?.value ?? null,
    };
  });

  return {
    id: previous?.id ?? hostId,
    type: definition.type,
    position: previous?.position ?? { x: 0, y: 0 },
    size: previous?.size ?? definition.defaultSize,
    zIndex: 0,
    config: {
      ...definition.defaultConfig,
      ...(previous?.config ?? {}),
      ...(config ?? {}),
    },
    inputs: baseInputs,
    outputs: baseOutputs,
    state:
      previous?.state ?? {
        loading: false,
      },
  };
};

export const WidgetHost: React.FC<WidgetHostProps> = ({
  type,
  config,
  inputs,
  onOutputsChange,
  onStateChange,
  disableCard,
  sx,
  contentSx,
}) => {
  const definition = React.useMemo(() => widgetRegistry.get(type), [type]);
  const hostId = React.useMemo(
    () => `host-${type}-${Math.random().toString(36).slice(2, 10)}`,
    [type]
  );

  const [widget, setWidget] = React.useState<Widget>(() => {
    if (!definition) {
      throw new Error(`Unknown widget type "${type}"`);
    }
    return createWidgetInstance(definition, hostId, config, inputs);
  });

  React.useEffect(() => {
    if (!definition) return;
    setWidget((prev) => createWidgetInstance(definition, hostId, config, inputs, prev));
  }, [definition, hostId, config, inputs]);

  const emitOutputs = React.useCallback(
    (outputs: Widget['outputs']) => {
      if (!onOutputsChange) return;
      const map: Record<string, any> = {};
      for (const output of outputs) {
        map[output.id] = output.value;
      }
      onOutputsChange(map);
    },
    [onOutputsChange]
  );

  const handleUpdateConfig = React.useCallback(
    (nextConfig: Partial<WidgetConfig>) => {
      setWidget((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          ...nextConfig,
        },
      }));
    },
    []
  );

  const handleUpdateOutputs = React.useCallback(
    (partial: Partial<Record<string, any>>) => {
      setWidget((prev) => {
        const nextOutputs = prev.outputs.map((output) =>
          Object.prototype.hasOwnProperty.call(partial, output.id)
            ? { ...output, value: partial[output.id] }
            : output
        );
        emitOutputs(nextOutputs);
        return { ...prev, outputs: nextOutputs };
      });
    },
    [emitOutputs]
  );

  const handleUpdateState = React.useCallback(
    (partial: Partial<WidgetState>) => {
      setWidget((prev) => {
        const nextState = { ...prev.state, ...partial };
        if (onStateChange) {
          onStateChange(nextState);
        }
        return { ...prev, state: nextState };
      });
    },
    [onStateChange]
  );

  const handleUpdateInputs = React.useCallback(
    (partial: Partial<Record<string, any>>) => {
      setWidget((prev) => {
        const nextInputs = prev.inputs.map((input) =>
          Object.prototype.hasOwnProperty.call(partial, input.id)
            ? { ...input, value: partial[input.id] }
            : input
        );
        return { ...prev, inputs: nextInputs };
      });
    },
    []
  );

  if (!definition) {
    return (
      <Alert severity="error" sx={{ height: '100%' }}>
        Unknown widget type “{type}”.
      </Alert>
    );
  }

  const Component = definition.component as React.ComponentType<WidgetProps>;
  const widgetProps: WidgetProps = {
    widget,
    onUpdateConfig: handleUpdateConfig,
    onUpdateOutputs: handleUpdateOutputs,
    onUpdateState: handleUpdateState,
    onUpdateInputs: handleUpdateInputs,
    isSelected: false,
    canvasMode: CanvasMode.DESKTOP,
  };

  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...contentSx }}>
      <Component {...widgetProps} />
    </Box>
  );

  if (disableCard) {
    return content;
  }

  return (
    <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
      {content}
    </Paper>
  );
};
